import type { RTCIceServerLike } from '../../shared/protocol';

// ICE servers in two steps. TURN over TCP/TLS gets through strict firewalls, but on many networks
// each of those allocations stalls the browser's WebRTC thread for seconds (measured with
// Cloudflare's list: ~25 s with all of them, 0.3 s with the UDP ones, ~4 s with TLS on 443 alone).
// So a connection starts with STUN + UDP TURN and, only if it does not connect, adds the single
// most firewall-proof TCP/TLS entry (TLS on port 443 looks like ordinary HTTPS).

const urlsOf = (s: RTCIceServerLike) => ([] as string[]).concat(s.urls);
const isTcpTurn = (url: string) => /^turns:/i.test(url) || /[?&]transport=tcp/i.test(url);
const isTurn = (url: string) => /^turns?:/i.test(url);

/** STUN and UDP TURN only; the full list when that would leave no TURN server at all. */
export function fastIceServers(servers: RTCIceServerLike[]): RTCIceServerLike[] {
  const fast = servers.map((s) => ({ ...s, urls: urlsOf(s).filter((u) => !isTcpTurn(u)) })).filter((s) => s.urls.length > 0);
  const hasTurn = (list: RTCIceServerLike[]) => list.some((s) => urlsOf(s).some(isTurn));
  return hasTurn(servers) && !hasTurn(fast) ? servers : fast;
}

function tcpRank(url: string): number {
  if (/^turns:.*:443\b/i.test(url)) return 0;
  if (/^turns:/i.test(url)) return 1;
  if (/^turn:.*:443\?.*transport=tcp/i.test(url)) return 2;
  return 3;
}

/** Fast list plus the best TCP/TLS TURN entry, or null when there is nothing to add. */
export function fallbackIceServers(servers: RTCIceServerLike[]): RTCIceServerLike[] | null {
  const fast = fastIceServers(servers);
  if (fast === servers) return null;
  let best: { server: RTCIceServerLike; url: string } | null = null;
  for (const server of servers) {
    for (const url of urlsOf(server).filter(isTcpTurn)) {
      if (!best || tcpRank(url) < tcpRank(best.url)) best = { server, url };
    }
  }
  if (!best) return null;
  const { urls: _urls, ...credentials } = best.server;
  return [...fast, { ...credentials, urls: [best.url] }];
}
